import {
  createProgressiveRequestHandler,
  createRequestHandler,
} from "../utils/requestHandlers";

import { MediaBaseRouteDelete } from "./media";
import { cfAPI } from "../api";
import { createProgressHandler } from "../utils/createProgressHandler";

// Action Types
export const STAFF_REQUEST = "STAFF_REQUEST";
export const STAFF_ERROR = "STAFF_ERROR";
export const STAFFS_FETCH = "STAFFS_FETCH";
export const STAFF_FETCH = "STAFF_FETCH";
export const STAFF_CREATE = "STAFF_CREATE";
export const STAFF_EDIT = "STAFF_EDIT";
export const STAFF_DELETE = "STAFF_DELETE";
export const STAFF_SEARCH = "STAFF_SEARCH";
export const STAFF_DELETE_IMAGE = "STAFF_DELETE_IMAGE";

// Download Action Types for staff
const STAFF_DOWNLOAD_START = "STAFF_DOWNLOAD_START";
const STAFF_DOWNLOAD_UPDATE = "STAFF_DOWNLOAD_UPDATE";
const STAFF_DOWNLOAD_FINISH = "STAFF_DOWNLOAD_FINISH";

const BaseRoute = "companyapi/api/v1/staffs";

// Reusable action types object for consistency
const actionTypes = {
  fetches: {
    request: STAFF_REQUEST,
    success: STAFFS_FETCH,
    error: STAFF_ERROR,
  },
  fetch: { request: STAFF_REQUEST, success: STAFF_FETCH, error: STAFF_ERROR },
  create: { request: STAFF_REQUEST, success: STAFF_CREATE, error: STAFF_ERROR },
  update: { request: STAFF_REQUEST, success: STAFF_EDIT, error: STAFF_ERROR },
  delete: { request: STAFF_REQUEST, success: STAFF_DELETE, error: STAFF_ERROR },
  deleteImage: {
    request: STAFF_REQUEST,
    success: STAFF_DELETE_IMAGE,
    error: STAFF_ERROR,
  },
  download: {
    request: STAFF_REQUEST,
    start: STAFF_DOWNLOAD_START,
    update: STAFF_DOWNLOAD_UPDATE,
    finish: STAFF_DOWNLOAD_FINISH,
    success: STAFFS_FETCH,
    error: STAFF_ERROR,
  },
};

const INITIAL_STATE = {
  loading: false,
  staffs: [],
  staff: {},
  error: "",
  downloadProgress: 0,
  isDownloading: false,
};

// Reusable handler for form data creation
const createStaffFormData = (values) => {
  const formData = new FormData();
  for (const key in values) {
    if (key !== "files") {
      formData.append(key, values[key] || "");
    }
  }
  if (values.files) {
    for (const file of values.files) {
      formData.append("files", file.file);
    }
  }
  formData.append("mediaType", "IMAGE");
  return formData;
};

// Actions using the new handlers
export const CreateStaff = (values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.post(BaseRoute, createStaffFormData(values)),
    actionTypes.create,
    "Profile Created"
  );
};

export const FetchStaffs = () => async (dispatch) => {
  await createProgressiveRequestHandler(
    dispatch,
    () =>
      cfAPI.get(BaseRoute, {
        onDownloadProgress: createProgressHandler(
          dispatch,
          actionTypes.download.update,
          actionTypes.download.finish
        ),
      }),
    actionTypes.download,
    createProgressHandler
  );
};

export const FetchStaff = (id) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.get(`${BaseRoute}/${id}`),
    actionTypes.fetch,
    null // No toast message for a fetch
  );
};

export const UpdateStaff = (id, values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.patch(`${BaseRoute}/${id}`, createStaffFormData(values)),
    actionTypes.update,
    "Profile Updated"
  );
};

export const DeleteStaffPicture =
  (name, fileName, values) => async (dispatch) => {
    await createRequestHandler(
      dispatch,
      () => cfAPI.delete(MediaBaseRouteDelete(name, fileName)),
      actionTypes.deleteImage,
      "Image Deleted"
    );
  };

export const DeleteStaff = (id, values) => async (dispatch) => {
  await createRequestHandler(
    dispatch,
    () => cfAPI.delete(`${BaseRoute}/${id}`),
    actionTypes.delete,
    "Profile Deleted"
  );
};

// Reducer with toasts removed and state logic updated
export const StaffsReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    // Download Cases
    case STAFF_DOWNLOAD_START:
      return { ...state, isDownloading: true, downloadProgress: 0 };
    case STAFF_DOWNLOAD_UPDATE:
      return { ...state, downloadProgress: action.payload };
    case STAFF_DOWNLOAD_FINISH:
      return { ...state, isDownloading: false, downloadProgress: 100 };

    // Request/Error Cases
    case STAFF_REQUEST:
      return { ...state, loading: true };
    case STAFF_ERROR:
      return { ...state, loading: false, error: action.payload };

    // Success Cases
    case STAFFS_FETCH:
      return { ...state, loading: false, staffs: action.payload };
    case STAFF_FETCH:
      return { ...state, loading: false, staff: action.payload };
    case STAFF_CREATE:
      return {
        ...state,
        loading: false,
        staffs: [...state.staffs, action.payload],
      };
    case STAFF_EDIT:
      const updatedList = state.staffs.map((staff) =>
        staff.id === action.payload.id ? action.payload : staff
      );
      return {
        ...state,
        loading: false,
        staff: action.payload,
        staffs: updatedList,
      };
    case STAFF_DELETE:
      const newList = state.staffs.filter((s) => s.id !== action.payload.id);
      return { ...state, loading: false, staffs: newList };
    case STAFF_DELETE_IMAGE:
      // Assuming payload has the updated staff object without the deleted image
      return { ...state, loading: false, staff: action.payload };

    default:
      return state;
  }
};
